import { useState, useEffect, useRef } from 'react';
import { Check, X } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import PaymentModal from './PaymentModal';

const Pricing = ({ onContactClick }) => {
  const { t } = useTranslation();
  const [isYearly, setIsYearly] = useState(true);
  const [isVisible, setIsVisible] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [isPaymentModalOpen, setIsPaymentModalOpen] = useState(false);
  const sectionRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) setIsVisible(true);
      },
      { threshold: 0.1 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  const plans = [
    {
      id: 'starter',
      name: t('pricing.starterName'),
      monthlyPrice: 124,
      yearlyPrice: 99,
      description: t('pricing.starterDesc'),
      features: [
        // Included features first
        { text: t('pricing.starterFeature1'), included: true },
        { text: t('pricing.starterFeature2'), included: true },
        { text: t('pricing.starterFeature4'), included: true },
        { text: t('pricing.starterFeature5'), included: true },
        { text: t('pricing.starterFeature6'), included: true },
        { text: t('pricing.starterFeature7'), included: true },
        { text: t('pricing.starterFeature8'), included: true },
        { text: t('pricing.starterFeature9'), included: true },
        { text: t('pricing.starterFeature10'), included: true },
        { text: t('pricing.starterFeature14'), included: true },
        { text: t('pricing.aiCandidateSearch'), included: true },
        { text: t('pricing.aiOfferSuggestion'), included: true },
        // Excluded features last
        { text: t('pricing.starterFeature11'), included: false },
        { text: t('pricing.starterFeature12'), included: false },
        { text: t('pricing.starterFeature13'), included: false },
        { text: t('pricing.starterFeature15'), included: false },
        { text: t('pricing.starterFeature16'), included: false },
        { text: t('pricing.starterFeature17'), included: false },
        { text: t('pricing.starterFeature18'), included: false },
      ],
      cta: t('pricing.starterCta'),
      popular: false,
    },
    {
      id: 'professional',
      name: t('pricing.professionalName'),
      monthlyPrice: 1874,
      yearlyPrice: 1499,
      description: t('pricing.professionalDesc'),
      features: [
        // Included features first
        { text: t('pricing.professionalFeature1'), included: true },
        { text: t('pricing.textInterview300'), included: true },
        { text: t('pricing.professionalFeature2'), included: true },
        { text: t('pricing.professionalFeature4'), included: true },
        { text: t('pricing.professionalFeature5'), included: true },
        { text: t('pricing.professionalFeature6'), included: true },
        { text: t('pricing.professionalFeature7'), included: true },
        { text: t('pricing.professionalFeature8'), included: true },
        { text: t('pricing.professionalFeature9'), included: true },
        { text: t('pricing.professionalFeature10'), included: true },
        { text: t('pricing.professionalFeature11'), included: true },
        { text: t('pricing.professionalFeature12'), included: true },
        { text: t('pricing.professionalFeature13'), included: true },
        { text: t('pricing.professionalFeature15'), included: true },
        { text: t('pricing.whatsappInterview'), included: true },
        { text: t('pricing.aiCandidateSearch'), included: true },
        { text: t('pricing.aiOfferSuggestion'), included: true },
        // Excluded features last
        { text: t('pricing.professionalFeature14'), included: false },
        { text: t('pricing.professionalFeature16'), included: false },
        { text: t('pricing.professionalFeature17'), included: false },
        { text: t('pricing.professionalFeature18'), included: false },
        { text: t('pricing.professionalFeature19'), included: false },
      ],
      cta: t('pricing.professionalCta'),
      popular: true,
    },
    {
      id: 'enterprise',
      name: t('pricing.enterpriseName'),
      monthlyPrice: t('pricing.custom'),
      yearlyPrice: t('pricing.custom'),
      description: t('pricing.enterpriseDesc'),
      features: [
        { text: t('pricing.enterpriseFeature1'), included: true },
        { text: t('pricing.textInterview300'), included: true },
        { text: t('pricing.enterpriseFeature2'), included: true },
        { text: t('pricing.enterpriseFeature3'), included: true },
        { text: t('pricing.enterpriseFeature4'), included: true },
        { text: t('pricing.enterpriseFeature5'), included: true },
        { text: t('pricing.enterpriseFeature6'), included: true },
        { text: t('pricing.enterpriseFeature7'), included: true },
        { text: t('pricing.enterpriseFeature8'), included: true },
        { text: t('pricing.enterpriseFeature9'), included: true },
        { text: t('pricing.enterpriseFeature10'), included: true },
        { text: t('pricing.enterpriseFeature11'), included: true },
        { text: t('pricing.enterpriseFeature12'), included: true },
        { text: t('pricing.whatsappInterview'), included: true },
        { text: t('pricing.aiCandidateSearch'), included: true },
        { text: t('pricing.aiOfferSuggestion'), included: true },
      ],
      cta: t('pricing.enterpriseCta'),
      popular: false,
    },
  ];

  return (
    <section ref={sectionRef} id="pricing" className="section-spacing bg-gradient-to-b from-gray-50 to-white relative overflow-hidden">
      {/* Decorative background elements */}
      <div className="absolute inset-0 opacity-5">
        <div className="absolute top-20 left-10 w-72 h-72 bg-primary rounded-full blur-3xl"></div>
        <div className="absolute bottom-20 right-10 w-96 h-96 bg-accent-teal rounded-full blur-3xl"></div>
      </div>

      <div className="container-custom relative z-10 px-4 sm:px-6">
        {/* Section Header */}
        <div className={`text-center mb-10 sm:mb-12 md:mb-16 transition-all duration-700 ${
          isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
        }`}>
          <h2 className="font-heading text-2xl sm:text-3xl md:text-4xl lg:text-5xl text-gray-900 mb-3 sm:mb-4">
            {t('pricing.title')}
          </h2>
          <p className="text-base sm:text-lg text-gray-600 max-w-2xl mx-auto mb-6 sm:mb-8 px-2">
            {t('pricing.subtitle')}
          </p>

          {/* Billing Toggle */}
          <div className={`inline-flex items-center rounded-lg sm:rounded-xl border border-primary overflow-hidden transition-all duration-500 ${
            isVisible ? 'opacity-100 scale-100' : 'opacity-0 scale-90'
          }`} style={{ transitionDelay: '200ms' }}>
            <button
              onClick={() => setIsYearly(false)}
              className={`px-4 sm:px-6 py-2 sm:py-2.5 text-xs sm:text-sm font-button transition-all duration-200 touch-manipulation ${
                !isYearly
                  ? 'bg-primary text-white'
                  : 'bg-white text-gray-900'
              }`}
            >
              {t('pricing.monthlyPayment')}
            </button>
            <button
              onClick={() => setIsYearly(true)}
              className={`px-4 sm:px-6 py-2 sm:py-2.5 text-xs sm:text-sm font-button transition-all duration-200 border-l border-primary touch-manipulation ${
                isYearly
                  ? 'bg-primary text-white'
                  : 'bg-white text-gray-900'
              }`}
            >
              {t('pricing.yearlyPayment')} <span className="text-xs font-normal"> {t('pricing.savePercent')}</span>
            </button>
          </div>
        </div>

        {/* Pricing Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 sm:gap-8 lg:gap-6 max-w-7xl mx-auto">
          {plans.map((plan, index) => (
            <div
              key={index}
              className={`relative bg-white rounded-xl sm:rounded-2xl shadow-lg hover:shadow-xl transition-all duration-500 ${
                plan.popular
                  ? 'border-2 border-primary md:scale-105 md:-mt-4 md:mb-4'
                  : 'border border-gray-200'
              } ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-12'}`}
              style={{ transitionDelay: `${300 + index * 150}ms` }}
            >
              {plan.popular && (
                <div className={`absolute -top-3 sm:-top-4 left-1/2 transform -translate-x-1/2 transition-all duration-500 ${
                  isVisible ? 'opacity-100 scale-100' : 'opacity-0 scale-75'
                }`} style={{ transitionDelay: `${500 + index * 150}ms` }}>
                  <span className="bg-primary text-white px-3 sm:px-4 py-1 rounded-full text-xs font-label">
                    {t('pricing.professionalPopular')}
                  </span>
                </div>
              )}

              <div className="p-5 sm:p-6 md:p-8">
                {/* Plan Name */}
                <h3 className="text-xl sm:text-2xl font-bold text-gray-900 mb-2">{plan.name}</h3>

                {/* Price */}
                <div className="mb-3 sm:mb-4">
                  {typeof plan.monthlyPrice === 'number' ? (
                    <>
                      <span className="text-3xl sm:text-4xl font-bold text-gray-900">
                        ${isYearly ? plan.yearlyPrice : plan.monthlyPrice}
                      </span>
                      <span className="text-gray-600 ml-2 text-sm sm:text-base">{t('pricing.perMonth')}</span>
                    </>
                  ) : (
                    <span className="text-3xl sm:text-4xl font-bold text-gray-900">{plan.monthlyPrice}</span>
                  )}
                </div>

                {/* Description */}
                <p className="text-gray-600 text-xs sm:text-sm mb-4 sm:mb-6 min-h-[2.5rem] sm:min-h-[3rem]">{plan.description}</p>

                {/* CTA Button */}
                <button
                  className={`w-full py-2.5 sm:py-3 px-4 sm:px-6 rounded-lg sm:rounded-xl font-button transition-all duration-300 mb-4 sm:mb-6 hover:-translate-y-0.5 touch-manipulation text-sm sm:text-base ${
                    plan.id === 'professional'
                      ? 'bg-primary text-white hover:bg-primary-dark shadow-lg hover:shadow-xl'
                      : 'bg-gray-100 text-gray-900 hover:bg-gray-200 border-2 border-gray-300'
                  }`}
                  onClick={() => {
                    if (plan.id === 'enterprise' && onContactClick) {
                      onContactClick();
                    } else if (typeof plan.monthlyPrice === 'number') {
                      setSelectedPlan(plan);
                      setIsPaymentModalOpen(true);
                    }
                  }}
                >
                  {plan.cta}
                </button>

                {/* Features */}
                <div className="space-y-2 sm:space-y-3">
                  {plan.features.map((feature, featureIndex) => (
                    <div key={featureIndex} className="flex items-start gap-2 sm:gap-3">
                      <div className="flex-shrink-0 mt-0.5">
                        {feature.included ? (
                          <Check className="w-4 h-4 sm:w-5 sm:h-5 text-accent-teal" />
                        ) : (
                          <X className="w-4 h-4 sm:w-5 sm:h-5 text-gray-300" />
                        )}
                      </div>
                      <span className={`text-xs sm:text-sm ${feature.included ? 'text-gray-700' : 'text-gray-400 line-through'}`}>
                        {feature.text}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          ))}
        </div>

      </div>

      {/* Payment Modal */}
      <PaymentModal
        isOpen={isPaymentModalOpen}
        onClose={() => {
          setIsPaymentModalOpen(false);
          setSelectedPlan(null);
        }}
        plan={selectedPlan}
        isYearly={isYearly}
      />
    </section>
  );
};

export default Pricing;
